import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';

const MAX_SHOWN = 60;

function rank(options: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  const starts: string[] = [];
  const word: string[] = [];
  const within: string[] = [];
  for (const o of options) {
    const l = o.toLowerCase();
    if (l.startsWith(q)) starts.push(o);
    else if (l.includes(` ${q}`)) word.push(o);
    else if (l.includes(q)) within.push(o);
  }
  return [...starts, ...word, ...within];
}

function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim().toLowerCase();
  const i = q ? text.toLowerCase().indexOf(q) : -1;
  if (i < 0) return <span>{text}</span>;
  // One span, because the option row is a flex container: separate text nodes would become flex items and lose their edge spaces.
  return <span>{text.slice(0, i)}<strong>{text.slice(i, i + q.length)}</strong>{text.slice(i + q.length)}</span>;
}

interface Props {
  id: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  recent?: string[];
}

export function CouncilPicker({ id, options, value, onChange, disabled, placeholder, recent = [] }: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = useId();

  // With nothing typed (or a council already chosen) offer the recently used ones first, then everything.
  const browsing = !value.trim() || options.includes(value);
  const { items, recentCount, total } = useMemo(() => {
    if (!browsing) {
      const matches = rank(options, value);
      return { items: matches.slice(0, MAX_SHOWN), recentCount: 0, total: matches.length };
    }
    const recents = recent.filter(r => options.includes(r));
    return { items: [...recents, ...options.slice(0, MAX_SHOWN)], recentCount: recents.length, total: recents.length + options.length };
  }, [options, recent, value, browsing]);
  const shown = items;

  useEffect(() => { setActive(0); }, [value]);
  useEffect(() => {
    // Scroll only the dropdown itself; scrollIntoView would also move the page.
    const ul = list.current;
    const el = ul?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    if (!ul || !el) return;
    if (el.offsetTop < ul.scrollTop) ul.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > ul.scrollTop + ul.clientHeight) ul.scrollTop = el.offsetTop + el.offsetHeight - ul.clientHeight;
  }, [active, open]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && open && shown[active]) { e.preventDefault(); pick(shown[active]); }
    else if (e.key === 'Escape') setOpen(false);
  }

  return (
    <div className="sx-combo" ref={root}>
      <input
        id={id} className="sx-input" type="text" role="combobox" aria-expanded={open} aria-controls={listId}
        aria-autocomplete="list" value={value} disabled={disabled} placeholder={placeholder}
        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => {
          setOpen(true);
          // On touch devices the keyboard covers the list, so lift the field; with a mouse there's no keyboard and no need to move the page.
          if (window.matchMedia('(hover: none)').matches) {
            window.setTimeout(() => root.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250);
          }
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && (
        <ul id={listId} ref={list} className="sx-combo-list" role="listbox" onMouseDown={e => e.preventDefault()}>
          {shown.length === 0 && <li className="sx-combo-empty">No local planning authority matches “{value.trim()}”</li>}
          {shown.map((o, i) => (
            <Fragment key={i < recentCount ? `recent:${o}` : o}>
              {recentCount > 0 && (i === 0 || i === recentCount) && (
                <li className="sx-combo-head" role="presentation">{i === 0 ? 'Recently used' : 'All local planning authorities'}</li>
              )}
              <li
              data-idx={i} role="option" aria-selected={i === active}
              className={`sx-combo-opt${i === active ? ' is-active' : ''}${o === value ? ' is-picked' : ''}`}
              onClick={() => pick(o)} onMouseEnter={() => setActive(i)}
            >
                <Highlight text={o} query={browsing ? '' : value} />
              </li>
            </Fragment>
          ))}
          {total > shown.length && (
            <li className="sx-combo-more">{total - shown.length} more — keep typing to narrow</li>
          )}
        </ul>
      )}
    </div>
  );
}
